import {
  Component, OnInit, Inject, ViewEncapsulation, OnDestroy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { isNil } from 'lodash';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import dayjs from 'dayjs';
import { Subject, firstValueFrom } from 'rxjs';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { I18nToastProvider } from 'src/providers/i18n-toast.provider';
import { DoenkidsSessionProvider } from 'src/providers/session.provider';
import { TranslateService } from 'src/app/utils/translate.service';
import { getTimezoneForCountryCode, toRfc3339FromLocalDate } from 'src/app/utils/timezone';

interface ICopyProgramDialogFields {
  name: string;
  to: string;
  from: string;
}

export interface IPossibleCopyProgramLocation {
  id: number;
  name: string;
}

export interface ICopyProgramDialogData extends ICopyProgramDialogFields {
  locations: IPossibleCopyProgramLocation[];
}

export interface ICopyProgramDialogResult extends ICopyProgramDialogFields {
  locations: IPossibleCopyProgramLocation[];
}

@Component({
    selector: 'app-copy-program-dialog',
    templateUrl: './copy-program-dialog.component.html',
    styleUrls: ['./copy-program-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class CopyProgramDialogComponent implements OnInit, OnDestroy {
  public form: UntypedFormGroup;

  private stop$ = new Subject();
  private organizationTimezone = 'UTC';

  constructor(
    fb: UntypedFormBuilder,
    private $i18nToastProvider: I18nToastProvider,
    private $session: DoenkidsSessionProvider,
    private $translateService: TranslateService,
    public dialogRef: MatDialogRef<CopyProgramDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ICopyProgramDialogData,
  ) {
    this.form = fb.group({
      name: ['', Validators.required],
      to: ['', Validators.required],
      from: ['', Validators.required],
    });
  }

  ngOnDestroy(): void {
    this.stop$.next(undefined);
  }

  async ngOnInit() {
    if (isNil(this.data)) {
      console.warn('No data passed to dialog, returning.');
      this.dialogRef.close();
      return;
    }

    this.organizationTimezone = getTimezoneForCountryCode(
      await firstValueFrom(this.$session.ouCountryCode$),
      this.$translateService.currentLang,
    );

    this.form.patchValue({
      name: this.data.name,
      to: this.toDatepickerDate(this.data.to),
      from: this.toDatepickerDate(this.data.from),
    });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  getDatepickerMinDate(value?: string): Date | null {
    const parsedDate = dayjs(value);

    if (!parsedDate.isValid()) {
      return null;
    }

    return parsedDate.startOf('day').toDate();
  }

  getDatepickerMaxDate(value?: string): Date | null {
    const parsedDate = dayjs(value);

    if (!parsedDate.isValid()) {
      return null;
    }

    return parsedDate.endOf('day').toDate();
  }

  private toDatepickerDate(value?: string | Date | null): Date | null {
    const parsedDate = dayjs(value);

    if (!parsedDate.isValid()) {
      return null;
    }

    return parsedDate.startOf('day').toDate();
  }

  confirm() {
    if (!this.form.valid) {
      console.error('copy dialog form error', this.form.errors);
      this.$i18nToastProvider.error(_('dialog.copy_program.invalid_form'));
    }

    const formValue = this.form.value;

    const result: ICopyProgramDialogResult = {
      locations: this.data.locations,
      name: formValue.name,
      to: toRfc3339FromLocalDate(dayjs(formValue.to).format('YYYY-MM-DD'), this.organizationTimezone, 18),
      from: toRfc3339FromLocalDate(dayjs(formValue.from).format('YYYY-MM-DD'), this.organizationTimezone, 8, 30),
    };

    this.dialogRef.close(result);
  }
}
